define([
    '../Util/defaultValue',
    '../Util/defined',
    '../Util/encode',
    '../Util/callbackSystem',
    '../System/ColorObj',
    '../System/Vec3Obj',
    '../Enum/EnumRippleEditMode',
    '../Enum/EnumRippleModulationMode'
], function (
    defaultValue,
    defined,
    encode,
    callbackSystem,
    ColorObj,
    Vec3Obj,
    EnumRippleEditMode,
    EnumRippleModulationMode
) {
    'use strict'

    /**
     * 波纹效果。
     * <br /><br />
     * <div align='center'>
     * <img src='images/Ripple.gif' width='900' height='650' /><br />
     * 波纹效果图
     * </div>
     * 
     * @demo {@link http://lbs.zjkelan.com/Ripple.html|CooWasm Ripple demo}
     * @alias Ripple
     * @constructor
     *
     * @param {Object} props 包含如下属性的Object：
     * @param {EnumRippleEditMode} [props.editMode=EnumRippleEditMode.CREATE] 编辑状态，如果location为空，则需要通过鼠标点击场景创建；如果location不为空，则为展示模式。
     * @param {EnumRippleModulationMode} [props.modulationMode=EnumRippleModulationMode.EXPANSION] 波纹模式。
     * @param {Vec3Obj} [props.location] 圆心地理坐标位置。
     * @param {ColorObj} [props.color=new ColorObj(1.0, 0.0, 0.0, 0.8)] 颜色。
     * @param {Number} [props.radius=10.0] 圆心到最外圈半径，单位：米。
     * @param {Number} [props.speed=5.0] 波纹速度，单位：m/s。
     * @param {Number} [props.topDistance=10.0] 垂直方向上，波纹效果上底面距离圆心距离（底面高度高于圆心），单位：米。
     * @param {Number} [props.bottomDistance=10.0] 垂直方向上，波纹效果下底面距离圆心距离（底面高度低于圆心），单位：米。
     * @param {Number} [props.minDistance=1.0] 最小可视距离。
     * @param {Number} [props.maxDistance=2000.0] 最大可视距离。
     * @param {Number} [props.minAltitude=0.0] 最小可见高度。
     * @param {Number} [props.maxAltitude=3.4028234663852886e+38] 最大可见高度。
     * @param {Boolean} [props.bVisible=true] 显隐状态。
     * @param {CallbackBoolean} [callback=null] 加载成功后触发的函数回调。
     * @example
     * var ripple = new CooWasm.Ripple({},(state)=>{console.log("load status:" + state)})
     */
    class Ripple {
        /**
         * @description 编辑状态，如果location为空，则需要通过鼠标点击场景创建；如果location不为空，则为展示模式。
         * @type {EnumRippleEditMode}
         */
        editMode
        /**
         * @description 波纹模式。
         * @type {EnumRippleModulationMode}
         */
        modulationMode
        /**
         * @description 圆心地理坐标位置。
         * @type {Vec3Obj}
         */
        location
        /**
         * @description 颜色。
         * @type {ColorObj}
         */
        color
        /**
         * @description 圆心到最外圈半径，单位：米。
         * @type {Number}
         */
        radius
        /**
         * @description 波纹速度，单位：m/s。
         * @type {Number}
         */
        speed
        /**
         * @description 垂直方向上，波纹效果上底面距离圆心距离（底面高度高于圆心），单位：米。
         * @type {Number}
         */
        topDistance
        /**
         * @description 垂直方向上，波纹效果下底面距离圆心距离（底面高度低于圆心），单位：米。
         * @type {Number}
         */
        bottomDistance
        /**
         * @description 最小可视距离。
         * @type {Number}
         */
        minDistance
        /**
         * @description 最大可视距离。
         * @type {Number}
         */
        maxDistance
        /**
         * @description 最小可见高度。
         * @type {Number}
         */
        minAltitude
        /**
         * @description 最大可见高度。
         * @type {Number}
         */
        maxAltitude
        /**
         * @description 显隐状态。
         * @type {Boolean}
         * @default true
         */
        bVisible = true

        /**
         * @description 波纹对象uuid。
         * @type {String}
         * @readonly
         */
        avatarUuid = null
        /**
         * @description 波纹组件uuid。
         * @type {String}
         * @readonly
         */
        componentDynamicAlertWaveUuid = null
        /**
         * @description 高度控制组件uuid。
         * @type {String}
         * @readonly
         */
        componentAltitudeVisCtrlUuid = null
        /**
         * @description Ripple在CooWasm中类名。
         * @private
         * @readonly
         */
        className = "Ripple"
        static avatarType = "deSMP.Avatar.EmptyAvatar"
        static componentDynamicAlertWaveType = "deSMP.AvatarComponent.DynamicAlertWaveComponent"
        static componentAltitudeVisCtrlType = "deSMP.AvatarComponent.AltitudeVisCtrlComponent"
        constructor(props, callback) {
            if (!defined(props)) {
                props = {}
            }
            if (defined(props.build) && props.build == true) {
                return
            }
            let bSync = true
            if (props.editMode == EnumRippleEditMode.SHOW) {
                if (!defined(props.location)) {
                    console.log("need point location!")
                    return
                }
            }
            //create avatars
            let avatarCategoryName = "deSMP.Avatar.EmptyAvatar"
            let componentCategoryName = Ripple.componentDynamicAlertWaveType + "#$%" + Ripple.componentAltitudeVisCtrlType + "#$%"
            let avatarProps = ""
            let componentProps = ""
            //avatar props
            avatarProps += this.toStr("bVisible", props.bVisible, "Visible")
            //component props
            let dynamicAlertWaveComponentProps = ""
            dynamicAlertWaveComponentProps += this.toStr("editMode", props.editMode, "EditMode")
            dynamicAlertWaveComponentProps += this.toStr("modulationMode", props.modulationMode, "RadialMode")
            dynamicAlertWaveComponentProps += this.toStr("location", props.location, "Point")
            dynamicAlertWaveComponentProps += this.toStr("color", props.color, "Color")
            dynamicAlertWaveComponentProps += this.toStr("radius", props.radius, "Radius")
            dynamicAlertWaveComponentProps += this.toStr("speed", props.speed, "Speed")
            dynamicAlertWaveComponentProps += this.toStr("topDistance", props.topDistance, "TopDistance")
            dynamicAlertWaveComponentProps += this.toStr("bottomDistance", props.bottomDistance, "BottomDistance")
            dynamicAlertWaveComponentProps += this.toStr("minDistance", props.minDistance, "MinVisibleDistance")
            dynamicAlertWaveComponentProps += this.toStr("maxDistance", props.maxDistance, "MaxVisibleDistance")
            componentProps += dynamicAlertWaveComponentProps + "#$%"
            let altitudeVisCtrlComponentProps = ""
            altitudeVisCtrlComponentProps += this.toStr("minAltitude", props.minAltitude, "MinVisibleAltitude")
            altitudeVisCtrlComponentProps += this.toStr("maxAltitude", props.maxAltitude, "MaxVisibleAltitude")
            componentProps += altitudeVisCtrlComponentProps + "#$%"
            var idInfo = Element.prototype.NativeAPI.LoadAvatarAndMultiComponent(encode(avatarCategoryName), encode(avatarProps), encode(componentCategoryName), encode(componentProps), bSync, this.bVisible, true)
            // var idInfo = Element.prototype.NativeAPI.LoadAvatarAndComponent(encode(""), aCategory, aName, encode(aProps), cCategory, cName, encode(cProps), this.bVisible, true)
            if (idInfo != "") {
                var idArray = idInfo.split('#$%')
                this.avatarUuid = idArray[0]
                this.componentDynamicAlertWaveUuid = idArray[1]
                this.componentAltitudeVisCtrlUuid = idArray[2]
                callbackSystem.register("LoadAvatarAndComponent", this.avatarUuid, (state) => {
                    if (this.editMode == EnumRippleEditMode.SHOW) {
                        this.getProperties()
                        if (defined(callback)) {
                            callback(state)
                        }
                        Element.prototype.Map.ripples.push(this)
                    } else {
                        callbackSystem.register("EditEnd", this.avatarUuid, (state) => {
                            this.getProperties()
                            if (defined(callback)) {
                                callback(true)
                            }
                            Element.prototype.Map.ripples.push(this)
                        })
                    }
                })
                //console.log("Ripple avatarUuid:" + this.avatarUuid + " componentDynamicAlertWaveUuid:" + this.componentDynamicAlertWaveUuid)
            }
        }

        /**
         * <p>
         * 更新单个或者多个对象属性，使之立即生效。
         * </p>
         *
         * @param {Object} [props] 包含如下属性的Object。
         * @param {EnumRippleEditMode} [props.editMode] 编辑状态，如果location为空，则需要通过鼠标点击场景创建；如果location不为空，则为展示模式。
         * @param {EnumRippleModulationMode} [props.modulationMode] 波纹模式。
         * @param {Vec3Obj} [props.location] 圆心地理坐标位置。
         * @param {ColorObj} [props.color] 颜色。
         * @param {Number} [props.radius] 圆心到最外圈半径，单位：米。
         * @param {Number} [props.speed] 波纹速度，单位：m/s。
         * @param {Number} [props.topDistance] 垂直方向上，波纹效果上底面距离圆心距离（底面高度高于圆心），单位：米。
         * @param {Number} [props.bottomDistance] 垂直方向上，波纹效果下底面距离圆心距离（底面高度低于圆心），单位：米。
         * @param {Number} [props.minDistance] 最小可视距离。
         * @param {Number} [props.maxDistance] 最大可视距离。
         * @param {Number} [props.minAltitude] 最小可见高度。
         * @param {Number} [props.maxAltitude] 最大可见高度。
         * @param {Boolean} [props.bVisible] 显隐状态。
         * @param {CallbackBoolean} [callback] 加载成功后触发的函数回调。
         * @example
         * ripple.update({
         *    bVisible:false
         * },(state)=>{console.log("update status:" + state)})
         */
        update(props, callback) {
            let avatarProps = ""
            let componentProps = ""
            let dynamicAlertWaveComponentProps = ""
            let altitudeVisCtrlComponentProps = ""
            let uuids = ""
            if (defined(props)) {
                avatarProps += this.toStr("bVisible", props.bVisible, "Visible")
                //dynamicAlertWaveComponent
                dynamicAlertWaveComponentProps += this.toStr("editMode", props.editMode, "EditMode")
                dynamicAlertWaveComponentProps += this.toStr("modulationMode", props.modulationMode, "RadialMode")
                dynamicAlertWaveComponentProps += this.toStr("location", props.location, "Point")
                dynamicAlertWaveComponentProps += this.toStr("color", props.color, "Color")
                dynamicAlertWaveComponentProps += this.toStr("radius", props.radius, "Radius")
                dynamicAlertWaveComponentProps += this.toStr("speed", props.speed, "Speed")
                dynamicAlertWaveComponentProps += this.toStr("topDistance", props.topDistance, "TopDistance")
                dynamicAlertWaveComponentProps += this.toStr("bottomDistance", props.bottomDistance, "BottomDistance")
                dynamicAlertWaveComponentProps += this.toStr("minDistance", props.minDistance, "MinVisibleDistance")
                dynamicAlertWaveComponentProps += this.toStr("maxDistance", props.maxDistance, "MaxVisibleDistance")
                //altitudeVisCtrlComponent
                altitudeVisCtrlComponentProps += this.toStr("minAltitude", props.minAltitude, "MinVisibleAltitude")
                altitudeVisCtrlComponentProps += this.toStr("maxAltitude", props.maxAltitude, "MaxVisibleAltitude")

                uuids += this.componentDynamicAlertWaveUuid + "#$%"
                uuids += this.componentAltitudeVisCtrlUuid + "#$%"
                componentProps += dynamicAlertWaveComponentProps + "#$%"
                componentProps += altitudeVisCtrlComponentProps + "#$%"

                if (this.editMode == EnumRippleEditMode.SHOW) {
                    callbackSystem.register("UpdateAvatarAndMultiComponent", this.avatarUuid, (state) => {
                        this.getProperties()
                        if (defined(callback)) {
                            callback(state)
                        }
                    })
                } else {
                    callbackSystem.register("EditEnd", this.avatarUuid, (state) => {
                        this.getProperties()
                        if (defined(callback)) {
                            callback(state)
                        }
                    })
                }
                Element.prototype.NativeAPI.UpdateAvatarAndMultiComponent(encode(this.avatarUuid), encode(uuids), encode(avatarProps), encode(componentProps), defined(callback))
                // Element.prototype.NativeAPI.UpdateAvatarAndComponent(encode(this.avatarUuid), encode(this.componentDynamicAlertWaveUuid), encode(avatarProps), encode(componentProps), defined(callback))
            }
        }

        toStr(memName, value, key) {
            let result = ""
            if (defined(value)) {
                this[memName] = value
                if (typeof value.toStr === 'function') {
                    result = key + ":" + value.toStr() + "|"
                } else {
                    result = key + ":" + value + "|"
                }
            }
            return result
        }
        /**
         * 从场景中定位自身(自动根据数据包围盒定位，不可修改定位参数)。
         * @param {CallbackBoolean} [callback=null] 飞行结束后触发的函数回调。
         * @example
         * Ripple.locate((state)=>{console.log("locate status:" + state)})
         */
        locate(callback) {
            callbackSystem.register("", "LocateAvatar", (state) => {
                if (defined(callback)) {
                    callback(state)
                }
            })
            Element.prototype.NativeAPI.LocateAvatar(encode(this.avatarUuid))
        }

        /**
         * 获取对象包含的属性。
         * @returns {Boolean} 是否获取成功。获取成功时，会自动赋值给属性。
         * @example
         * Ripple.getProperties()
         */
        getProperties() {
            if (!defined(this.avatarUuid)) {
                console.log("uuid require!")
                return false
            }
            let pAvatarUuid = Element.prototype.NativeAPI.Uuid_New()
            Element.prototype.NativeAPI.Uuid_SetId(pAvatarUuid, encode(this.avatarUuid))
            let pAvatar = Element.prototype.NativeAPI.MapImpl_getAvatar(pAvatarUuid)
            let avatarPropSize = Element.prototype.NativeAPI.Avatar_GetPropertiesSize(pAvatar)
            for (let i = 0; i < avatarPropSize; ++i) {
                let pProp = Element.prototype.NativeAPI.Avatar_GetProperty(pAvatar, i)
                let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                if (name == "Visible") {
                    if (value == "true" || value == "1") {
                        this.bVisible = true
                    } else {
                        this.bVisible = false
                    }
                }
            }
            let avatarAcSize = Element.prototype.NativeAPI.Avatar_GetAvatarComponentsSize(pAvatar)
            for (let j = 0; j < avatarAcSize; ++j) {
                let pAc = Element.prototype.NativeAPI.Avatar_GetAvatarComponent(pAvatar, j)
                let propSize = Element.prototype.NativeAPI.AvatarComponent_GetPropertiesSize(pAc)
                let pObjType = Element.prototype.NativeAPI.AvatarComponent_GetObjectType(pAc)
                let pName = Element.prototype.NativeAPI.ObjectType_GetName(pObjType)
                let pUuid = Element.prototype.NativeAPI.AvatarComponent_GetUuid(pAc)
                let uuid = Element.prototype.NativeAPI.Uuid_GetId(pUuid)
                if (pName == "DynamicAlertWaveComponent") {
                    this.componentDynamicAlertWaveUuid = uuid
                    for (let z = 0; z < propSize; ++z) {
                        let pProp = Element.prototype.NativeAPI.AvatarComponent_GetProperty(pAc, z)
                        let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                        let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                        if (name == "EditMode") {
                            this.editMode = Number(value)
                        } else if (name == "RadialMode") {
                            this.modulationMode = Number(value)
                        } else if (name == "Point") {
                            this.location = Vec3Obj.fromStr(value)
                        } else if (name == "Color") {
                            this.color = ColorObj.fromStr(value)
                        } else if (name == "Radius") {
                            this.radius = Number(value)
                        } else if (name == "Speed") {
                            this.speed = Number(value)
                        } else if (name == "TopDistance") {
                            this.topDistance = Number(value)
                        } else if (name == "BottomDistance") {
                            this.bottomDistance = Number(value)
                        } else if (name == "MinVisibleDistance") {
                            this.minDistance = Number(value)
                        } else if (name == "MaxVisibleDistance") {
                            this.maxDistance = Number(value)
                        }
                    }
                } else if (pName == "AltitudeVisCtrlComponent") {
                    this.componentAltitudeVisCtrlUuid = uuid
                    for (let z = 0; z < propSize; ++z) {
                        let pProp = Element.prototype.NativeAPI.AvatarComponent_GetProperty(pAc, z)
                        let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                        let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                        if (name == "MinVisibleAltitude") {
                            this.minAltitude = Number(value)
                        } else if (name == "MaxVisibleAltitude") {
                            this.maxAltitude = Number(value)
                        }
                    }
                }
            }
            Element.prototype.NativeAPI.Avatar_Delete(pAvatar)
            Element.prototype.NativeAPI.Uuid_Delete(pAvatarUuid)
            return true
        }
        /**
         * 从场景中销毁自身。
         * @param {CallbackBoolean} [callback=null] 销毁对象后触发的函数回调。
         * @returns null 空值。
         * @example
         * Ripple = Ripple.destroy((state)=>{console.log("destroy status:" + state)})
         */
        destroy(callback) {
            if (!defined(this.avatarUuid)) {
                console.log("avatarUuid is null!")
                return null
            }
            callbackSystem.register("RemoveAvatar", this.avatarUuid, (state) => {
                if (defined(callback)) {
                    callback(state)
                }
                Element.prototype.Map.ripples = Element.prototype.Map.ripples.filter(item => item !== this)
                callbackSystem.remove("LoadAvatarAndComponent", this.avatarUuid)
                callbackSystem.remove("UpdateAvatarAndMultiComponent", this.avatarUuid)
                callbackSystem.remove("EditEnd", this.avatarUuid)
                callbackSystem.remove("", "LocateAvatar")
                callbackSystem.remove("RemoveAvatar", this.avatarUuid)
                this.avatarUuid = null
                this.componentDynamicAlertWaveUuid = null
                this.componentAltitudeVisCtrlUuid = null
            })
            Element.prototype.NativeAPI.RemoveAvatar(encode(this.avatarUuid))
            return null
        }
    }
    return Ripple
})